import { BrowserRouter, Routes, Route, NavLink, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './App.css';
import {
  ProfileProvider,
  useProfileContext,
} from './hooks/useProfileContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import ThemeToggle from './components/ThemeToggle';
import DevBanner from './components/DevBanner';
import LanguageSelector from './components/LanguageSelector';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import UsersPage from './pages/UsersPage';
import AcceptInvitationPage from './pages/AcceptInvitationPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ShareRiderPage from './pages/ShareRiderPage';
import AdvancingSharePage from './pages/AdvancingSharePage';
import PromoKitSharePage from './pages/PromoKitSharePage';
import AccountPage from './pages/AccountPage';
import PricingPage from './pages/PricingPage';
import BandSelector from './components/BandSelector';
import BandzLogo from './components/BandzLogo';
import RequireLayoutAccess from './components/RequireLayoutAccess';
// L3 (2026-10-12) — PAGE guard: a page the sidebar hides must not open by URL.
// It reads `pageVisibility` (the same source as the nav/preview) and touches NO
// server mount: the read/write protection stays server-side.
import RequirePageAccess from './components/RequirePageAccess';
// L3b (2026-10-12) — PLATFORM-ADMIN guard: `/users`, `/api-keys`,
// `/manager-onboarding` were only behind `RequireAuth` (session only), so any
// signed-in account opened them by URL. `RequirePageAccess` cannot be used here
// (`pageVisibility('users'|'apiKeys')` is false for EVERYONE, the superadmin
// included — the `admin` section is rendered on `isSuperAdmin || isShowcase`).
import RequireSuperadmin from './components/RequireSuperadmin';
// The single source for « is this page refused to this member? » — read by the
// sidebar here AND by the preview in `memberPreview.ts`, so they cannot disagree.
import { PAGE_GROUPS, groupVisible, pageVisibility, type PageGroupDef } from './lib/pageAccess';
import { roleBadgeKey, roleBadgeClass } from './lib/roles';
import BandListPage from './pages/BandListPage';
import HomePage from './pages/HomePage';
import RiderPage from './pages/RiderPage';
import RiderEditorPage from './pages/RiderEditorPage';
import DaysheetLayoutEditorPage from './pages/DaysheetLayoutEditorPage';
import TourBookLayoutEditorPage from './pages/TourBookLayoutEditorPage';
import ApiKeysPage from './pages/ApiKeysPage';
import ManagerOnboardingPage from './pages/ManagerOnboardingPage';
import MusicianListPage from './pages/MusicianListPage';
import MusicianEditPage from './pages/MusicianEditPage';
import CrewListPage from './pages/CrewListPage';
import CrewEditPage from './pages/CrewEditPage';
import ConfigSoundPage from './pages/ConfigSoundPage';
import ConfigIemPage from './pages/ConfigIemPage';
import ConfigHospitalityPage from './pages/ConfigHospitalityPage';
import ConfigLightsPage from './pages/ConfigLightsPage';
import ConfigGeneralPage from './pages/ConfigGeneralPage';
import ConfigStageDesignPage from './pages/ConfigStageDesignPage';
import ConfigAudioSpecsPage from './pages/ConfigAudioSpecsPage';
import ConfigBacklinePage from './pages/ConfigBacklinePage';
import BandSettingsPage from './pages/BandSettingsPage';
import StagePlotEditorPage from './pages/StagePlotEditorPage';
import NotFoundPage from './pages/NotFoundPage';
import { STAGEPLOT_EDITOR_ROUTE } from './lib/routeContracts';
import ShowsPage from './pages/ShowsPage';
import ItineraryPage from './pages/ItineraryPage';
import AtlasPage from './pages/AtlasPage';
import DayOfPage from './pages/DayOfPage';
// P2e — contextual band landing (Day-of if a show is today, else Atlas).
import BandLandingPage from './pages/BandLandingPage';
import AdvancingHubPage from './pages/AdvancingHubPage';
import ShowTimePage from './pages/ShowTimePage';
import TourListPage from './pages/TourListPage';
import PayoutsPage from './pages/PayoutsPage';
import ShowDetailPage from './pages/ShowDetailPage';
import VenuesPage from './pages/VenuesPage';
import VenueEditPage from './pages/VenueEditPage';
import ContactsPage from './pages/ContactsPage';
import ContactEditPage from './pages/ContactEditPage';
import ContactImportReviewPage from './pages/ContactImportReviewPage';
import ShowsImportReviewPage from './pages/ShowsImportReviewPage';
import PersonalCalendarPage from './pages/PersonalCalendarPage';
import TeamPage from './pages/TeamPage';
import AvailabilityPage from './pages/AvailabilityPage';
import TeamEditPage from './pages/TeamEditPage';
import SetlistsPage from './pages/SetlistsPage';
import ChecklistsPage from './pages/ChecklistsPage';
import SongbookPage from './pages/SongbookPage';
import PreloadOfflinePage from './pages/PreloadOfflinePage';
import AiAssistantPage from './pages/AiAssistantPage';
import HelpPage from './pages/HelpPage';
import WelcomeWizardPage from './pages/WelcomeWizardPage';
import ErrorBoundary from './components/ErrorBoundary';
import SaveErrorBanner from './components/SaveErrorBanner';
import ConfigGuardBanner from './components/ConfigGuardBanner';
import TrialBanner from './components/TrialBanner';
import MustChangePassword from './components/MustChangePassword';
import ShowcaseBanner from './components/ShowcaseBanner';
import OfflineBadge from './components/OfflineBadge';
import CookieConsent from './components/CookieConsent';
import MobileTabBar from './components/MobileTabBar';
import SidebarBrandLink, { sidebarHomeHref } from './components/SidebarBrandLink';
// P2b — universal command palette (⌘K / Ctrl+K) + its sidebar entry point.
import CommandPalette, { PaletteOpenButton } from './components/CommandPalette';
import { ensureConnected, disconnect } from './lib/realtime';
import { autoPreloadBand } from './lib/autoPreload';

/**
 * Invisible bridge that owns the real-time socket lifecycle for the app:
 * connects the WebSocket while signed in, tears it down on logout, and
 * auto-preloads (into the offline cache) the currently active band's data.
 */
function RealtimeBridge() {
  const { user } = useAuth();
  const { activeProfileId } = useProfileContext();

  useEffect(() => {
    if (user) ensureConnected();
    else disconnect();
  }, [user]);

  useEffect(() => {
    if (activeProfileId) autoPreloadBand(activeProfileId);
  }, [activeProfileId]);

  return null;
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const { t } = useTranslation();
  if (loading) {
    return <div className="page-pad">{t('app.loading')}</div>;
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

// Legacy /bands/:slug redirect → the current Band Settings page. The old
// BandEditPage is dead (it read an id, not a slug, and hung on Loading).
function RedirectBandToSettings() {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/band/${slug}/settings`} replace />;
}

/**
 * Legacy `Audio & Backline` config URL (v0.645): the page was split into
 * `Audio specs` and `Backline`. Redirect the old URL to the Audio specs page
 * while PRESERVING the query string, so the Rider Layout editor's deep links
 * (`?focus=<fieldId>`) and the user's bookmarks keep landing on the right
 * field.
 */
function RedirectAudioBackline() {
  const { bandSlug } = useParams<{ bandSlug: string }>();
  const { search } = useLocation();
  return <Navigate to={`/band/${bandSlug}/config/audio-specs${search}`} replace />;
}

function UserPanel() {
  const { user, logout } = useAuth();
  const { activeProfileId, profiles } = useProfileContext();
  const navigate = useNavigate();
  const { t } = useTranslation();
  if (!user) return null;
  // WHAT THE BADGE SHOWS (user request 2026-10-08): the role the person HAS in the
  // active band — never the technical write-guard ceiling on `users.role`. A real
  // member saw the badge "read-only" next to his name while he was the band's
  // technician: technically true (his account may not write elsewhere), confusing
  // as a label. Priority: the shared demo, the platform admin, then the band role.
  const badgeInput = {
    isShowcase: user.is_showcase,
    globalRole: user.global_role,
    bandRole: profiles.find((p) => p.id === activeProfileId)?.my_role,
  };
  const badgeKey = roleBadgeKey(badgeInput);
  const badgeLabel = badgeKey ? t(badgeKey) : null;
  async function onLogout() {
    await logout();
    navigate('/login', { replace: true });
  }
  return (
    <div className="user-panel">
      <div className="user-info">
        <span className="user-name">{user.nickname || user.username}</span>
        {badgeLabel && (
          /* `title` keeps the technical state reachable for whoever needs it. */
          <span
            className={`user-role ${roleBadgeClass(badgeInput)}`}
            title={user.role === 'read-only' ? t('roles.readOnlyAccountHint') : undefined}
          >
            {badgeLabel}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', gap: 6, width: '100%' }}>
        <ThemeToggle />
        <LanguageSelector />
      </div>
      <div style={{ display: 'flex', gap: 6, width: '100%' }}>
        <button className="logout-btn" style={{ flex: 1 }} onClick={() => navigate('/account')}>
          {t('app.account')}
        </button>
        <button className="logout-btn" style={{ flex: 1 }} onClick={onLogout}>
          {t('app.logout')}
        </button>
      </div>
    </div>
  );
}

// Collapsible sidebar section: the label toggles its children open/closed, and
// the open state is persisted per-user in localStorage so it survives reloads.
function SidebarSection({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
  const storageKey = `bandz.sidebar.section.${id}`;
  const [open, setOpen] = useState<boolean>(() => {
    try { return localStorage.getItem(storageKey) !== 'closed'; } catch { return true; }
  });
  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem(storageKey, next ? 'open' : 'closed'); } catch { /* ignore */ }
  };
  return (
    <section className="sidebar-section">
      <button
        type="button"
        onClick={toggle}
        className="nav-section-label"
        aria-expanded={open}
      >
        <span>{label}</span>
        <svg
          className={`nav-section-chevron ${open ? 'open' : ''}`}
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M4 6l3 3 3-3"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && children}
    </section>
  );
}

// L1 (2026-10-12) — UNE SECTION DONT TOUS LES ITEMS SONT CACHÉS DISPARAÎT ENTIÈREMENT
// (en-tête compris). La sidebar ne code plus ses sections en dur : elle ITÈRE
// `PAGE_GROUPS` (ordre, libellés, routes — la même table que l'aperçu « ce que voit X »
// et que la palette) et n'affiche une section que si `groupVisible` voit au moins un de
// ses items. Avant, l'en-tête « Rider Config » était rendu inconditionnellement : un
// `viewer` voyait une section ne contenant QUE « Rider preview » — une section fantôme
// qui lui mentait sur ce qu'il pouvait faire.
export function SidebarGroupNav({
  group, vis, base, onNavigate, keepAll = false, headerExtra = null,
}: {
  group: PageGroupDef;
  vis: Record<string, boolean>;
  base: string;
  onNavigate: () => void;
  /** Sans bande active, la section Tour reste listée (liens historiques). */
  keepAll?: boolean;
  /** Entrées hors `PAGE_GROUPS` rendues en TÊTE de la section (Atlas / Day of). */
  headerExtra?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const shown = (id: string) => keepAll || !!vis[id];
  // LA règle de section : au moins un item visible, sinon RIEN (en-tête compris).
  // EXCEPTION (surface viewer v2, 2026-10-20) : la section Tour porte la ROUTE MAP
  // (Atlas) en `headerExtra`, HORS `PAGE_GROUPS` — un viewer dont tous les items
  // Tour sont cachés rend quand même la section, réduite au seul lien Route map
  // (cliquer un événement y ouvre la LECTURE du show). Les autres groupes, sans
  // headerExtra, gardent la règle stricte.
  if (!keepAll && !groupVisible(vis, group) && !headerExtra) return null;
  return (
    <SidebarSection id={group.id} label={t(group.labelKey)}>
      <nav className="sidebar-nav">
        {headerExtra}
        {group.pages.map((page) => {
          // HORS MENU (`hidden`, 2026-10-17) : page gardée + URL directe, mais jamais listée.
          if (page.hidden) return null;
          if (!shown(page.id)) return null;
          // `globalTo` = chemin global (section Tour, /ai-assistant) ; `to` = chemin
          // relatif au groupe, préfixé par `base` — jamais `/band//…`.
          const to = page.globalTo ?? `${base}/${page.to ?? ''}`;
          const cls = `nav-link ${page.subKey ? 'nav-link-with-sub ' : ''}`;
          return (
            <NavLink
              key={page.id}
              to={to}
              end={page.end}
              className={({ isActive }) => `${cls}${isActive ? 'active' : ''}`}
              onClick={onNavigate}
            >
              {page.subKey ? (
                <>
                  <span className="nav-link-label">{t(page.labelKey)}</span>
                  <span className="nav-link-subtitle">{t(page.subKey)}</span>
                </>
              ) : (
                t(page.labelKey)
              )}
            </NavLink>
          );
        })}
      </nav>
    </SidebarSection>
  );
}

function AppLayout() {
  const { activeProfileId, setActiveProfile, getProfile, profiles } = useProfileContext();
  const { user } = useAuth();
  const { t } = useTranslation();
  // Platform admin = superadmin (global_role). The legacy write-role
  // ('admin') only grants band write access, NOT the platform admin menu.
  const isSuperAdmin = user?.global_role === 'superadmin';
  // Read-only SHOWCASE (demo) account: it browses every page of the product
  // (configs, stage plot, layout editors, platform admin) with SAMPLE data, and
  // never writes (the autosave is cut + the server refuses). (2026-09-15)
  const isShowcase = !!user?.is_showcase;
  const location = useLocation();

  // Build URL base from band slug (not ID) for readable URLs
  const activeProfile = activeProfileId ? getProfile(activeProfileId) : null;
  const bandSlug = activeProfile?.slug || activeProfileId || '';
  const base = bandSlug ? `/band/${bandSlug}` : '';
  // LOGO-HOME (2026-11-04) — ONE destination shared by the sidebar brand link
  // and the mobile Home tab, so the two can never disagree: the band landing
  // (Day-of when a show is today, Route map otherwise), the multi-band Route
  // map when no band is open, the band grid when the account has none.
  // `base` is non-empty exactly when a band is active.
  const homeHref = sidebarHomeHref(base, profiles.length);
  // Current user's role on the ACTIVE band ('owner'|'editor'|'viewer'). A
  // superadmin has implicit full access on every band regardless of the
  // per-band link, so treat them as owner.
  const bandRole = isSuperAdmin ? 'owner' : activeProfile?.my_role;
  // The showcase account sees every band link (read-only tour of the product).
  // Finance gate (owner/superadmin always true; editor/viewer only when the
  // owner granted a `finance.*` capability) — hides the 💰 Payouts link. The
  // fine-grained rules live in the pages (Payouts = fees, the tour budget =
  // finance.budget, the show Finance tab = expenses/fees). (chantier C)
  // Layout gate (owner/superadmin always true; editor/viewer only when the owner
  // granted a `layout.*` capability) — controls the "Layout editor" sidebar
  // section AND the page guard of the 3 layout routes. ONE expression, shared
  // with the editors through `canEditLayoutsFor` / `canEditLayoutFor` so the UI
  // cannot drift from the server rule (`canWriteLayout` in server/authz.js).
  // Each LINK is shown on its own capability: a member granted only
  // `layout.daysheet` never sees a rider-layout entry that would refuse to open.
  // (2026-09-25)
  // RIDER CONTENT gate — the section that holds the 8 technical config pages and
  // the stage-plot editor. It is driven by the CAPABILITY `content.rider`, exactly
  // like the layout section above and exactly like the server: `PUT /api/bands/:id/
  // rider/config` (and /rider/:key) is exempted for a holder even when their ACCOUNT
  // is globally `read-only` — which is the case of 12 of the 15 prod accounts.
  // ⚠️ The old gate was `canEditBand` (ROLE only) → the very people allowed to edit
  // the rider could not see the pages (real case 2026-10-08: Seb, `tech` role,
  // holds the 8 rider points, only saw the preview). A `tech` holds them BY ROLE,
  // so `can()` sees them; an `editor` keeps access through the role branch.
  // The `/rider` PREVIEW link stays outside THIS gate (every band member may look at
  // the rider) but NOT outside the deny: a member refused the rider content gets a
  // 403 from `/api/bands/:id/rider`, so the link follows `pageVisible('riderPreview')`.
  // ── The explicit read DENY (the « — » level) ────────────────────────────────
  // Until 2026-10-08 the sidebar ignored it: a member refused the setlists still
  // saw the link and got the server's 403 on click. The nav now reads the SAME
  // single source as the preview. It hides ONLY what the server refuses (see
  // `pageAccess.ts`): an owner/editor is never denied, a refusal is per member.
  // ── LA SOURCE UNIQUE (2026-10-08) ───────────────────────────────────────────
  // Chaque item de la sidebar lit `vis`, la MÊME table que l'aperçu « ce que voit X »
  // calcule avec `pageVisibility` (pageAccess.ts). Les prédicats ad hoc qui vivaient
  // ici (rôle seul, capacité, refus) ont été fondus dedans : une règle de visibilité
  // ne peut plus exister à deux endroits, donc plus de dérive nav ↔ aperçu.
  const vis = pageVisibility({
    isSuperAdmin,
    isShowcase,
    bandRole,
    permissions: activeProfile?.permissions,
    denied: activeProfile?.denied,
    linked: !!activeProfileId,
  });
  // L1 (2026-10-12) — UNE SECTION VIDE DISPARAÎT ENTIÈREMENT (en-tête compris) : la
  // décision est prise par `SidebarGroupNav`, qui itère PAGE_GROUPS et n'affiche une
  // section que si `groupVisible(vis, group)` voit au moins un de ses items. La MÊME
  // table `vis` alimente les items et l'aperçu « ce que voit X » — aucune seconde règle.
  // On direct navigation to a /band/:slug/* page, resolve the slug to the active band
  useEffect(() => {
    const m = location.pathname.match(/^\/band\/([^/]+)/);
    if (!m) return;
    const urlSlug = m[1];
    const currentSlug = activeProfile?.slug || activeProfileId || '';
    if (urlSlug && urlSlug !== currentSlug && urlSlug !== activeProfileId) {
      setActiveProfile(urlSlug);
    }
  }, [location.pathname, activeProfile?.slug, activeProfileId, setActiveProfile]);
  const [menuOpen, setMenuOpen] = useState(false);
  // P2b — the command palette is a single open/closed state owned by the shell
  // (opened by ⌘K/Ctrl+K inside the palette, or by the sidebar search button).
  const [paletteOpen, setPaletteOpen] = useState(false);
  // Mobile layout (< 900px) is where the sidebar collapses off-screen. Only there
  // should we unmount the sidebar from the AT/focus tree when closed — never on
  // desktop, where it is always visible and focusable.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)');
    const on = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);

  // Close the mobile sidebar with the Escape key (WCAG focus/escape handling).
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  // Note-2 (2026-08-28): the first-login auto-redirect to /welcome is REMOVED.
  // The Welcome tour is still reachable via HelpPage (button "Take the tour").
  // Previously this gate bounced every authenticated user to /welcome until
  // 'bandz.welcome.seen' was set, which surprised returning admins. The tour
  // stays as a manual, opt-in experience in Help.

  // New-version banner: the SW auto-reload usually handles deploys by itself,
  // but if it ever misses (tab sat idle, controller edge case), show a manual
  // "Reload" so the user never has to hard-refresh every tab.
  const [updateAvailable, setUpdateAvailable] = useState(false);
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onControllerChange = () => setUpdateAvailable(true);
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
    return () => navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
  }, []);

  // Show the current build version in the footer (from the index.html meta,
  // which already carries the "vNNN" prefix).
  useEffect(() => {
    const el = document.getElementById('app-footer-ver');
    const meta = document.querySelector('meta[name="bandz-version"]');
    if (el && meta) el.textContent = meta.getAttribute('content') || '';
  }, []);

  return (
    <div className="app-layout">
      <RealtimeBridge />
      <ShowcaseBanner />
      {updateAvailable && (
        <div className="update-banner" role="status">
          <span>{t('app.newVersion')}</span>
          <button className="btn btn-primary" style={{ padding: '4px 12px', fontSize: 'var(--font-sm)' }} onClick={() => window.location.reload()}>
            {t('app.reloadNow')}
          </button>
        </div>
      )}
      <button
        className="mobile-menu-btn"
        onClick={() => setMenuOpen(!menuOpen)}
        aria-label={t('app.toggleMenu')}
        aria-expanded={menuOpen}
        aria-controls="app-sidebar"
      >
        <span className={`hamburger ${menuOpen ? 'open' : ''}`}>
          <span />
          <span />
          <span />
        </span>
      </button>

      <aside id="app-sidebar" className={`sidebar ${menuOpen ? 'open' : ''}`} aria-hidden={isMobile && !menuOpen || undefined} inert={isMobile && !menuOpen || undefined}>
        <div className="sidebar-header">
          {/* LOGO-HOME (2026-11-04) — the mark is the click-through to the
              operational home (band landing → Day-of / Route map). The drawer
              closes on click so mobile never lands on a page behind it. */}
          <SidebarBrandLink to={homeHref} onNavigate={() => setMenuOpen(false)} />
          <BandSelector />
        </div>

        <UserPanel />

        {/* L1 (2026-10-12) — LA SIDEBAR ITÈRE `PAGE_GROUPS` : ordre, libellés et routes
            viennent de la source unique, et une section dont tous les items sont cachés
            n'est PAS rendue du tout (son en-tête compris). `admin` reste à part : elle est
            pilotée par isSuperAdmin/isShowcase, pas par la visibilité de page. */}
        {PAGE_GROUPS.filter((g) => g.id !== 'admin').map((group) => (
          <SidebarGroupNav
            key={group.id}
            group={group}
            vis={vis}
            base={base}
            onNavigate={() => setMenuOpen(false)}
            // Sans bande active, seule la section Tour reste listée (comportement
            // historique) : les autres n'ont aucune page visible et disparaissent.
            keepAll={group.id === 'tour' && !activeProfileId}
            headerExtra={group.id === 'tour' ? (
              <NavLink
                to={base ? `${base}/atlas` : '/tour/atlas'}
                className={({ isActive }) =>
                  `nav-link ${isActive || location.pathname === '/tour/atlas' ? 'active' : ''}`
                }
                onClick={() => setMenuOpen(false)}
              >
                {t('sidebar.atlas')}
              </NavLink>
            ) : null}
          />
        ))}







        {(isSuperAdmin || isShowcase) && (
          <SidebarSection id="admin" label={t('sidebar.admin')}>
            <nav className="sidebar-nav">
              <NavLink
                to="/users"
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                onClick={() => setMenuOpen(false)}
              >
                {t('sidebar.users')}
              </NavLink>
              <NavLink
                to="/api-keys"
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                onClick={() => setMenuOpen(false)}
              >
                {t('sidebar.apiKeys')}
              </NavLink>
              <NavLink
                to="/manager-onboarding"
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                onClick={() => setMenuOpen(false)}
              >
                {t('sidebar.connectAiAgent')}
              </NavLink>
            </nav>
          </SidebarSection>
        )}

        {/* P2b — command palette entry point, bottom of the sidebar. Mobile has
            no ⌘K, so this button (also reachable from the More drawer) is THE
            entry there. It closes the drawer first: the palette is the modal. */}
        <nav className="sidebar-nav">
          <PaletteOpenButton
            onClick={() => {
              setMenuOpen(false);
              setPaletteOpen(true);
            }}
          />
          <NavLink
            to="/help"
            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
            onClick={() => setMenuOpen(false)}
          >
            {t('sidebar.help')}
          </NavLink>
        </nav>
      </aside>

      {menuOpen && (
        <div
          className="sidebar-overlay"
          role="button"
          tabIndex={0}
          aria-label={t('app.closeMenu')}
          onClick={() => setMenuOpen(false)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setMenuOpen(false); } }}
        />
      )}

      <main className="main-content">
        <OfflineBadge />
        <SaveErrorBanner />
        <ConfigGuardBanner />
        <TrialBanner />
        <MustChangePassword />
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          {/* Self-service sign-up must stay OUTSIDE RequireAuth: the visitor has
              no account yet, and a successful submit deliberately creates NO
              session (the e-mail must be verified first). (L3, 2026-09-24) */}
          <Route path="/signup" element={<SignupPage />} />
          {/* E-mail verification link target. Public for the same reason: the
              visitor arrives from the e-mail without a session, and the SESSION
              IS CREATED BY THE SERVER when the token is valid. (L3, 2026-09-24) */}
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          {/* Invitation acceptance must stay OUTSIDE RequireAuth: the invitee
              has no account yet, so a redirect to /login would trap them. */}
          <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
          {/* Reset must also be public: the user cannot sign in to reach it. */}
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          {/* Public rider validation: the promoter has no account, so the share
              page must live OUTSIDE RequireAuth (token in the URL = access). */}
          <Route path="/share/:token" element={<ShareRiderPage />} />
          {/* /share/rider/:token (3 segments — used by the advancing share's
              rider_share_token) NEVER matched the 2-segment route above and fell
              into the catch-all → RequireAuth → /login (pre-existing bug; the
              option-A fusion redirects it to the unified advancing page anyway,
              but the route must exist for the redirect to ever run). */}
          <Route path="/share/rider/:token" element={<ShareRiderPage />} />
          {/* Public advancing validation: no account, token in the URL carries
              the link's scope (tech/hotel/logistics/all). */}
          <Route path="/share/advancing/:token" element={<AdvancingSharePage />} />
          {/* Public Promo kit: no account, one token per band. Placed after the
              advancing route but before the generic /share/:token is fine because
              React Router ranks the static "promo" segment higher. (2026-08-22) */}
          <Route path="/share/promo/:token" element={<PromoKitSharePage />} />

          {/* Admin-only user management. L3b (2026-10-12) — `RequireSuperadmin`:
              superadmin OR the demo account, the SAME verdict the sidebar `admin`
              section uses. Before, `RequireAuth` alone let ANY signed-in account
              open these pages and only the API refused (broken screen). */}
          <Route
            path="/users"
            element={
              <RequireAuth>
                <RequireSuperadmin>
                  <UsersPage />
                </RequireSuperadmin>
              </RequireAuth>
            }
          />
          <Route
            path="/api-keys"
            element={
              <RequireAuth>
                <RequireSuperadmin>
                  <ApiKeysPage />
                </RequireSuperadmin>
              </RequireAuth>
            }
          />
          <Route
            path="/manager-onboarding"
            element={
              <RequireAuth>
                <RequireSuperadmin>
                  <ManagerOnboardingPage />
                </RequireSuperadmin>
              </RequireAuth>
            }
          />

          {/* Everything else is protected */}
          <Route
            path="/*"
            element={
              <RequireAuth>
                <Routes>
                  {/* Band management */}
                  <Route path="/bands" element={<BandListPage />} />
                  {/* Legacy: /bands/:slug was the old band editor, superseded by
                      /band/:slug/settings (Band Settings). Redirect so the old
                      URL never opens the dead/loading BandEditPage. (2026-08-12) */}
                  <Route path="/bands/:slug" element={<RedirectBandToSettings />} />

                  {/* Band-scoped routes — static :bandSlug path; pages wait for profile resolution */}
                  <Route path="/band/:bandSlug/rider" element={<RiderPage />} />
                  <Route path="/band/:bandSlug/rider-layout" element={<RequireLayoutAccess permission="rider"><RiderEditorPage /></RequireLayoutAccess>} />
                  <Route path="/band/:bandSlug/daysheet-layout" element={<RequireLayoutAccess permission="daysheet"><DaysheetLayoutEditorPage /></RequireLayoutAccess>} />
                  <Route path="/band/:bandSlug/shows" element={<ShowsPage />} />
                  <Route path="/band/:bandSlug/atlas" element={<AtlasPage />} />
                  <Route path="/band/:bandSlug/day-of" element={<DayOfPage />} />
                  {/* P2e — "home of a band": decides between the two pages
                      above (show today → Day-of, else Atlas) then redirects.
                      A real sub-route (never the naked /band/:bandSlug), so
                      the catch-all `*` → /home below is untouched. */}
                  <Route path="/band/:bandSlug/home" element={<BandLandingPage />} />
                  {/* L3 — page guards read `pageVisibility` (the nav/preview source).
                      Wrapped: settings, musicians, crew, availability, setlists,
                      songbook, checklists, payouts, stageplot-editor, the 8 config/*
                      pages, and (L3b) ai-assistant. NOT wrapped: the 3 layout routes
                      (RequireLayoutAccess), the public/share routes, and the Tour
                      pages visible to every member (shows, itinerary, advancing,
                      tours, team). `ai-assistant` uses `globalRoute` (mounted at the
                      root, no :bandSlug) so a `viewer` — for whom pageVisibility
                      hides it — is refused, like the nav already did. */}
                  <Route path="/band/:bandSlug/settings" element={<RequirePageAccess page="bandSettings"><BandSettingsPage /></RequirePageAccess>} />
                  <Route path="/band/:bandSlug/musicians" element={<RequirePageAccess page="musicians"><MusicianListPage /></RequirePageAccess>} />
                  <Route path="/band/:bandSlug/musicians/new" element={<RequirePageAccess page="musicians"><MusicianEditPage /></RequirePageAccess>} />
                  <Route path="/band/:bandSlug/musicians/:id" element={<RequirePageAccess page="musicians"><MusicianEditPage /></RequirePageAccess>} />
                  <Route path="/band/:bandSlug/crew" element={<RequirePageAccess page="crew"><CrewListPage /></RequirePageAccess>} />
                  <Route path="/band/:bandSlug/crew/new" element={<RequirePageAccess page="crew"><CrewEditPage /></RequirePageAccess>} />
                  <Route path="/band/:bandSlug/crew/:id" element={<RequirePageAccess page="crew"><CrewEditPage /></RequirePageAccess>} />
                  <Route path={STAGEPLOT_EDITOR_ROUTE} element={<RequirePageAccess page="stagePlot"><StagePlotEditorPage /></RequirePageAccess>} />
                  <Route path="/band/:bandSlug/setlists" element={<RequirePageAccess page="setlists"><SetlistsPage /></RequirePageAccess>} />
                  <Route path="/band/:bandSlug/songbook" element={<RequirePageAccess page="songbook"><SongbookPage /></RequirePageAccess>} />
                  <Route path="/band/:bandSlug/checklists" element={<RequirePageAccess page="checklists"><ChecklistsPage /></RequirePageAccess>} />
                  {/* Finance-gated: musician fees / payouts recap (per member). */}
                  <Route path="/band/:bandSlug/payouts" element={<RequirePageAccess page="payouts"><PayoutsPage /></RequirePageAccess>} />
                  <Route path="/band/:bandSlug/availability" element={<RequirePageAccess page="availability"><AvailabilityPage /></RequirePageAccess>} />
                  <Route path="/band/:bandSlug/config/sound" element={<RequirePageAccess page="configSound"><ConfigSoundPage /></RequirePageAccess>} />
                  <Route path="/band/:bandSlug/config/iem" element={<RequirePageAccess page="configIem"><ConfigIemPage /></RequirePageAccess>} />
                  <Route path="/band/:bandSlug/config/hospitality" element={<RequirePageAccess page="configHospitality"><ConfigHospitalityPage /></RequirePageAccess>} />
                  <Route path="/band/:bandSlug/config/lights" element={<RequirePageAccess page="configLights"><ConfigLightsPage /></RequirePageAccess>} />
                  <Route path="/band/:bandSlug/config/general" element={<RequirePageAccess page="configGeneral"><ConfigGeneralPage /></RequirePageAccess>} />
                  <Route path="/band/:bandSlug/config/audio-specs" element={<RequirePageAccess page="configAudioSpecs"><ConfigAudioSpecsPage /></RequirePageAccess>} />
                  <Route path="/band/:bandSlug/config/backline" element={<RequirePageAccess page="configBackline"><ConfigBacklinePage /></RequirePageAccess>} />
                  {/* Legacy URL — the old merged Audio & Backline page was split
                      in two (v0.645). Keep the redirect so bookmarks and the
                      layout editor deep links (?focus=) never 404. */}
                  <Route path="/band/:bandSlug/config/audio-backline" element={<RedirectAudioBackline />} />
                  <Route path="/band/:bandSlug/config/stage-design" element={<RequirePageAccess page="configStageDesign"><ConfigStageDesignPage /></RequirePageAccess>} />

                  {/* Tour routes */}
                  <Route path="/tour/shows" element={<ShowsPage />} />
                  {/* LOT A-MULTI-FIX (2026-11-04): `/tour/atlas` was never
                      declared — the Route map lived only under
                      `/band/:bandSlug/atlas`. Both now open AtlasPage; with
                      several accessible bands it starts on "All bands". */}
                  <Route path="/tour/atlas" element={<AtlasPage />} />
                  <Route path="/tour/itinerary" element={<ItineraryPage />} />
                  <Route path="/tour/advancing" element={<AdvancingHubPage />} />
                  <Route path="/tour/tours" element={<TourListPage />} />
                  <Route path="/tour/tourbook-layout" element={<RequireLayoutAccess permission="tourbook"><TourBookLayoutEditorPage /></RequireLayoutAccess>} />
                  <Route path="/tour/shows/import" element={<ShowsImportReviewPage />} />
                  <Route path="/tour/shows/new" element={<ShowDetailPage />} />
                  <Route path="/tour/shows/:id" element={<ShowDetailPage />} />
                  {/* SPEC-PERSONAL-CALENDAR (2026-10-21): /tour/calendar IS the
                      PERSONAL calendar now — the user's « concerned » shows +
                      his subscription banner. Reading only, every role
                      (viewer included); the group calendar view remains the
                      « Calendar » toggle inside ShowsPage. */}
                  <Route path="/tour/calendar" element={<PersonalCalendarPage />} />
                  <Route path="/tour/team" element={<TeamPage />} />
                  <Route path="/tour/team/new" element={<TeamEditPage />} />
                  <Route path="/tour/team/:id" element={<TeamEditPage />} />
                  {/* Venues/Contacts live in the Tour section (`/tour/…`, no
                      `:bandSlug`) — `globalRoute` tells the guard to resolve
                      pending from the active profile, not a URL slug. */}
                  <Route path="/tour/venues" element={<RequirePageAccess page="venues" globalRoute><VenuesPage /></RequirePageAccess>} />
                  <Route path="/tour/venues/new" element={<RequirePageAccess page="venues" globalRoute><VenueEditPage /></RequirePageAccess>} />
                  <Route path="/tour/venues/:id" element={<RequirePageAccess page="venues" globalRoute><VenueEditPage /></RequirePageAccess>} />
                  <Route path="/tour/contacts" element={<RequirePageAccess page="contacts" globalRoute><ContactsPage /></RequirePageAccess>} />
                  <Route path="/tour/contacts/import" element={<RequirePageAccess page="contacts" globalRoute><ContactImportReviewPage /></RequirePageAccess>} />
                  <Route path="/tour/contacts/new" element={<RequirePageAccess page="contacts" globalRoute><ContactEditPage /></RequirePageAccess>} />
                  <Route path="/tour/contacts/:id" element={<RequirePageAccess page="contacts" globalRoute><ContactEditPage /></RequirePageAccess>} />

                  {/* Landing page — neutral homepage with band grid.
                      Never auto-opens a band's rider. */}
                  <Route path="/account" element={<AccountPage />} />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/home" element={<HomePage />} />
                  <Route path="/preload" element={<PreloadOfflinePage />} />
                  <Route path="/ai-assistant" element={<RequirePageAccess page="aiAssistant" globalRoute><AiAssistantPage /></RequirePageAccess>} />
                  <Route path="/help" element={<HelpPage />} />
                  <Route path="/welcome" element={<WelcomeWizardPage />} />
                  <Route
                    path="/"
                    element={<Navigate to="/home" />}
                  />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </RequireAuth>
            }
          />
          {/* A silent failure IS a bug: any unknown URL — signed out included —
              must SAY that it does not exist instead of rendering nothing. */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>

      <footer className="app-footer">
        <div className="app-footer-inner">
          <span className="app-footer-brand">
            <BandzLogo variant="mark" className="app-footer-logo" />
            <strong>{t('app.nameFull')}</strong>
          </span>
          <span className="app-footer-tagline">
            {t('app.tagline')}
          </span>
          <span className="app-footer-links">
            <a href="https://bandzontour.com/privacy.html" target="_blank" rel="noopener noreferrer">{t('footer.privacy')}</a>
            <a href="https://bandzontour.com/terms.html" target="_blank" rel="noopener noreferrer">{t('footer.terms')}</a>
          </span>
          <span className="app-footer-ver" id="app-footer-ver">v0.469</span>
        </div>
      </footer>

      {/* P2a — mobile bottom tab bar (≤768px only, CSS media query).
          `More` opens the SAME off-canvas sidebar drawer rendered above, so
          every section/permission gate stays in exactly one place. */}
      <MobileTabBar onOpenMore={() => setMenuOpen(true)} moreOpen={menuOpen} homeHref={homeHref} />

      {/* P2b — the universal command palette (⌘K / Ctrl+K). One component,
          open/closed by state; it renders nothing while closed. */}
      <CommandPalette
        open={paletteOpen}
        onOpen={() => setPaletteOpen(true)}
        onClose={() => setPaletteOpen(false)}
        vis={vis}
      />

      {/* Informative cookie-consent banner (RGPD/nLPD). Fixed bottom overlay,
          shown until the user accepts; does NOT block the app. */}
      <CookieConsent />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <DevBanner />
      <ProfileProvider>
        <AuthProvider>
          <ErrorBoundary>
            <Routes>
              {/* Show Time runs OUTSIDE the AppLayout (sidebar) so it renders
                  as a full-screen, immersive, mobile-first live view for the
                  night of a show. It is still authenticated: the API calls it
                  makes (/api/shows/:id, roadmap) resolve band access from the
                  session. (2026-09-05) */}
              <Route
                path="/tour/show-time/:id"
                element={
                  <RequireAuth>
                    <ShowTimePage />
                  </RequireAuth>
                }
              />
              <Route path="/*" element={<AppLayout />} />
            </Routes>
          </ErrorBoundary>
        </AuthProvider>
      </ProfileProvider>
    </BrowserRouter>
  );
}